import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
// --- Import Redux
import { connect } from "react-redux";
// --- Import Functions
import useMakeRequest from "../../../hooks/useMakeRequest";
// components
import Preloader from "../../../components/UI/Preloader/Preloader";
import RefundTextField from "../RefundTextField/RefundTextField";
import RefundReasonSelect from "../RefundReasonSelect.js/RefundReasonSelect";
import RefundSelectProduct from "../RefundSelectProduct/RefundSelectProduct";
import RefundQuestionnaire from "../RefundQuestionnaire/RefundQuestionnaire";
import RefundErrorModal from "../RefundErrorModal/RefundErrorModal";
import RefundThankyouPage from "../RefundThankyouPage/RefundThankyouPage";
import RefundNoPurchases from "../RefundNoPurchases/RefundNoPurchases";
import RefundTextareaField from "../RefundTextareaField/RefundTextareaField";
// sass
import "./RefundForm.sass";

const INVALID = "Invalid";
const REQUIRED = "This is a required field.";
const maxCharacters = (number = 250) => {
  return `Maximum ${number} characters.`;
};

function getDate(timestamp) {
  const a = new Date(timestamp * 1000);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const year = a.getFullYear();
  const month = months[a.getMonth()];
  const date = a.getDate();

  const time = `${date} ${month} ${year}`;
  return time;
}

function getProducts(data) {
  const licenses = [];
  for (let i = 0; i < data.length; i += 1) {
    for (let j = 0; j < data[i].products.length; j += 1) {
      for (let l = 0; l < data[i].products[j].licenses.length; l += 1) {
        licenses.push({
          licenseId: data[i].products[j].licenses[l].id,
          licenseAmount: data[i].products[j].licenses[l].amount
            ? data[i].products[j].licenses[l].amount
            : 0,
          licenseCreatedTime: getDate(
            data[i].products[j].licenses[l].created_time
          ),
          display_name: data[i].products[j].item_name,
          product_key: data[i].products[j].product_key,
        });
      }
    }
  }
  return licenses;
}
function validateName(name) {
  const res = { isValid: true, error: "" };
  if (name.length <= 0) {
    res.isValid = false;
    res.error = REQUIRED;
    return res;
  }
  return res;
}
function validateEmail(email) {
  const res = { isValid: true, error: "" };
  if (email.length <= 0) {
    res.isValid = false;
    res.error = REQUIRED;
    return res;
  }
  if (
    !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/g.test(
      email
    )
  ) {
    res.isValid = false;
    res.error = INVALID;
    return res;
  }
  return res;
}
function validatePhoneNumber(phone) {
  const res = { isValid: true, error: "" };
  if (phone.length <= 0 || phone === "+") {
    res.isValid = false;
    res.error = REQUIRED;
    return res;
  }

  if (!/\d[()+-]?/g.test(phone)) {
    res.isValid = false;
    res.error = "Only digits starting with +";
    return res;
  }
  if (phone.match(/\d/g).length < 10) {
    res.isValid = false;
    res.error = INVALID;
    return res;
  }

  return res;
}
function validateSelectedProduct(value) {
  const res = { isValid: true, error: "" };
  if (!value || (value && !value.product_key)) {
    res.isValid = false;
    res.error = REQUIRED;
    return res;
  }
  return res;
}
function validateReasonRefund(value, otherValue) {
  const res = { isValid: true, otherIsValid: true, error: "" };
  if (value.value === "") {
    res.isValid = false;
    res.otherIsValid = true;
    res.error = REQUIRED;

    return res;
  }
  if (+value.value === 5 && otherValue?.length <= 0) {
    res.isValid = true;
    res.otherIsValid = false;
    res.error = REQUIRED;
    return res;
  }
  if (+value.value === 5 && otherValue?.length > 250) {
    res.isValid = true;
    res.otherIsValid = false;
    res.error = maxCharacters(250);
    return res;
  }

  return res;
}
function validateActivateDigitalKey(value) {
  const res = { isValid: true, error: "" };
  if (value === "") {
    res.isValid = false;
    res.error = REQUIRED;
    return res;
  }
  return res;
}
function validateAdditionNotes(value) {
  const res = { isValid: true, error: "" };
  if (value.length > 300) {
    res.isValid = false;
    res.error = maxCharacters(300);
    return res;
  }
  return res;
}
function validateApprovePolicy(value) {
  const res = { isValid: true, error: "" };
  if (value === false) {
    res.isValid = false;
    res.error = REQUIRED;
    return res;
  }
  return res;
}

const refundReasons = [
  { value: 1, label: "Didn’t receive the license" },
  { value: 2, label: "Wrong product purchased" },
  { value: 3, label: "Duplicated transaction" },
  { value: 4, label: "Unauthorized transaction" },
  { value: 5, label: "Other" },
];

function RefundForm({ userData }) {
  const [formValue, setFormValue] = useState({
    fullName: `${userData.first_name} ${userData.last_name}`,
    email: userData.email,
    phone: userData.phone,
    productToRefund: {},
    allUserProducts: [],
    refundReason: { label: "", value: "" },
    otherRefundReason: "",
    activateDigitalKey: "",
    additionNotes: "",
    approvePolicy: false,
  });
  const [formError, setFormError] = useState({
    fullName: "",
    email: "",
    phone: "",
    productToRefund: "",
    refundReason: "",
    otherRefundReason: "",
    activateDigitalKey: "",
    additionNotes: "",
    approvePolicy: "",
  });
  const [showError, setShowError] = useState(false);
  const [setParams, result, isLoading, hasError, error] = useMakeRequest();
  const [
    requestRefund,
    refundResult,
    refundIsLoading,
    refundHasError,
    refundError,
  ] = useMakeRequest();

  useEffect(() => {
    setParams({
      c: "accountStore",
      a: "userLicenses2",
    });
  }, []);

  function getRefundReason() {
    if (+formValue.refundReason.value === 5) {
      return formValue.otherRefundReason;
    }
    return formValue.refundReason.label;
  }

  function validateForm(newFormValue) {
    let res = true;
    const errors = {};
    const nameValid = validateName(newFormValue.fullName);
    const emailValid = validateEmail(newFormValue.email);
    const phoneValid = validatePhoneNumber(newFormValue.phone);
    const productSelected = validateSelectedProduct(
      newFormValue.productToRefund
    );
    const reasonRefund = validateReasonRefund(
      newFormValue.refundReason,
      newFormValue.otherRefundReason
    );
    const activateDigitalKey = validateActivateDigitalKey(
      newFormValue.activateDigitalKey
    );
    const additionNotes = validateAdditionNotes(newFormValue.additionNotes);
    const approvePolicy = validateApprovePolicy(newFormValue.approvePolicy);
    if (!nameValid.isValid) {
      errors.fullName = nameValid.error;
    } else {
      errors.fullName = "";
    }
    if (!emailValid.isValid) {
      errors.email = emailValid.error;
    } else {
      errors.email = "";
    }
    if (!phoneValid.isValid) {
      errors.phone = phoneValid.error;
    } else {
      errors.phone = "";
    }
    if (!productSelected.isValid) {
      errors.productToRefund = productSelected.error;
    } else {
      errors.productToRefund = "";
    }
    if (!reasonRefund.isValid) {
      errors.otherRefundReason = "";
      errors.refundReason = reasonRefund.error;
    } else if (!reasonRefund.otherIsValid) {
      errors.refundReason = "";
      errors.otherRefundReason = reasonRefund.error;
    } else {
      errors.refundReason = "";
      errors.otherRefundReason = "";
    }
    if (!activateDigitalKey.isValid) {
      errors.activateDigitalKey = activateDigitalKey.error;
    } else {
      errors.activateDigitalKey = "";
    }
    if (!additionNotes.isValid) {
      errors.additionNotes = additionNotes.error;
    } else {
      errors.additionNotes = "";
    }
    if (!approvePolicy.isValid) {
      errors.approvePolicy = approvePolicy.error;
    } else {
      errors.approvePolicy = "";
    }

    if (
      !nameValid.isValid ||
      !emailValid.isValid ||
      !phoneValid.isValid ||
      !productSelected.isValid ||
      !reasonRefund.isValid ||
      !reasonRefund.otherIsValid ||
      !activateDigitalKey.isValid ||
      !additionNotes.isValid ||
      !approvePolicy.isValid
    ) {
      res = false;
    } else {
      res = true;
    }

    setFormError({ ...errors });
    return res;
  }
  function checkformValid() {
    return Object.values(formError).every((value) => {
      if (typeof value === "object") {
        return Object.values(value).every((key) => key === "");
      }
      return value === "";
    });
  }

  function validateField(field, value, ...rest) {
    const validateFuncObj = {
      fullName: validateName,
      email: validateEmail,
      phone: validatePhoneNumber,
      productToRefund: validateSelectedProduct,
      refundReason: validateReasonRefund,
      otherRefundReason: validateReasonRefund,
      activateDigitalKey: validateActivateDigitalKey,
      additionNotes: validateAdditionNotes,
      approvePolicy: validateApprovePolicy,
    };
    if (!validateFuncObj[field]) {
      return;
    }
    const validateFunc = validateFuncObj[field];
    const { isValid, error: validError } = validateFunc(value, ...rest);
    if (!isValid) {
      setFormError({
        ...formError,
        [field]: validError,
      });
    } else {
      setFormError({
        ...formError,
        [field]: validError,
      });
    }
  }
  function hideErrorField(field) {
    setFormError({
      ...formError,
      [field]: "",
    });
  }

  const handleChange = (field, value) => {
    const newValue = {
      ...formValue,
      [field]: value,
    };
    setFormValue(newValue);
  };
  function submitForm(e) {
    e.preventDefault();
    if (!validateForm(formValue)) {
      return;
    }

    requestRefund({
      c: "front/Refund/Refund",
      a: "refundRequest",
      p: {
        guid: userData.guid,
        cid: userData.cid,
        license_id: formValue.productToRefund.licenseId,
        reason: getRefundReason(),
        additionalInformation: {
          name: formValue.fullName,
          email: formValue.email,
          phoneNumber: formValue.phone,
          questionnaire: {
            "Have you activated your Digital Key?":
              formValue.activateDigitalKey,
          },
          additionalNotes: formValue.additionNotes,
        },
      },
    });
  }

  useEffect(() => {
    if (!isLoading && result) {
      const products = getProducts(result);
      setFormValue({
        ...formValue,
        allUserProducts: products,
        refundReason: { label: "", value: "" },
      });
    }
  }, [result, isLoading]);

  useEffect(() => {
    if (!isLoading && hasError && error) {
      setFormError({ ...formError, productToRefund: error });
    }
  }, [formError, isLoading, hasError, error]);

  useEffect(() => {
    if (!refundIsLoading && refundHasError && refundError !== "") {
      setShowError(true);
    }
  }, [refundIsLoading, refundHasError, refundError]);

  if (isLoading && !result) {
    return (
      <div className="vh-100">
        <Preloader />
      </div>
    );
  }

  if (result && !result.length) {
    return <RefundNoPurchases />;
  }

  return (
    <>
      {refundResult ? (
        <RefundThankyouPage />
      ) : (
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8 refund">
            <div className="refund-navbar">
              <h3 className="refund-navbar__title">Refund Request Form</h3>
              <p className="refund-navbar__text-cid">
                Customer ID <span>{userData.cid}</span>
              </p>
            </div>
            <form className="form" onSubmit={submitForm}>
              <div className="form-field">
                <p className="form-field__title">
                  Full Name{" "}
                  <span className={formError.fullName !== "" ? "error" : ""}>
                    *
                  </span>
                </p>
                <RefundTextField
                  id="full_name"
                  value={formValue.fullName}
                  disabled={
                    !(
                      userData?.first_name === undefined ||
                      userData.first_name === ""
                    ) &&
                    !(
                      userData?.last_name === undefined ||
                      userData.last_name === ""
                    )
                  }
                  handleValue={(e) => {
                    handleChange("fullName", e.target.value);
                  }}
                  onBlur={() => {
                    validateField("fullName", formValue.fullName);
                  }}
                  onFocus={() => {
                    hideErrorField("fullName");
                  }}
                  placeholder="Enter your full name"
                  errorMsg={formError.fullName}
                />
              </div>
              <div className="form-field">
                <p className="form-field__title">
                  Email{" "}
                  <span className={formError.email !== "" ? "error" : ""}>
                    *
                  </span>
                </p>
                <RefundTextField
                  id="email"
                  value={formValue.email}
                  disabled={
                    !(userData?.email === undefined || userData.email === "")
                  }
                  handleValue={(e) => {
                    handleChange("email", e.target.value);
                  }}
                  onBlur={() => {
                    validateField("email", formValue.email);
                  }}
                  onFocus={() => {
                    hideErrorField("email");
                  }}
                  placeholder="Enter your email"
                  errorMsg={formError.email}
                />
              </div>
              <div className="form-field">
                <p className="form-field__title">
                  Phone Number{" "}
                  <span className={formError.phone !== "" ? "error" : ""}>
                    *
                  </span>
                </p>
                <RefundTextField
                  id="phone"
                  value={formValue.phone}
                  disabled={
                    !(userData?.phone === undefined || userData.phone === "")
                  }
                  handleValue={(e) => {
                    handleChange("phone", e.target.value);
                  }}
                  onBlur={() => {
                    validateField("phone", formValue.phone);
                  }}
                  onFocus={() => {
                    hideErrorField("phone");
                  }}
                  placeholder="Enter your phone number"
                  errorMsg={formError.phone}
                />
              </div>
              <div className="form-select">
                <p className="form-select__title">
                  Product Name{" "}
                  <span
                    className={formError.productToRefund !== "" ? "error" : ""}
                  >
                    *
                  </span>
                </p>
                {formValue.allUserProducts.length > 0 && (
                  <RefundSelectProduct
                    value={formValue.productToRefund}
                    placeholder="Choose the product  to refund"
                    options={formValue.allUserProducts}
                    onChange={(newValue) => {
                      handleChange("productToRefund", newValue);
                    }}
                    onBlur={() => {
                      validateField(
                        "productToRefund",
                        formValue.productToRefund
                      );
                    }}
                    onFocus={() => {
                      hideErrorField("productToRefund");
                    }}
                    errorMsg={formError.productToRefund}
                  />
                )}
              </div>
              <div className="form-select form-reason">
                <p className="form-select__title">
                  Refund request reason{" "}
                  <span
                    className={formError.refundReason !== "" ? "error" : ""}
                  >
                    *
                  </span>
                </p>

                <div className="form-reason__input-wrapper">
                  {refundReasons.length > 0 && (
                    <RefundReasonSelect
                      value={formValue.refundReason}
                      placeholder="Choose your reason for a refund"
                      options={refundReasons}
                      onChange={(newValue) => {
                        handleChange("refundReason", newValue);
                      }}
                      onBlur={() => {
                        validateField("refundReason", formValue.refundReason);
                      }}
                      onFocus={() => {
                        hideErrorField("refundReason");
                      }}
                      errorMsg={formError.refundReason}
                    />
                  )}
                  {+formValue.refundReason.value === 5 && (
                    <RefundTextareaField
                      id="otherRefundReason"
                      value={formValue.otherRefundReason}
                      handleValue={(e) => {
                        handleChange("otherRefundReason", e.target.value);
                        validateField(
                          "otherRefundReason",
                          formValue.refundReason,
                          e.target.value
                        );
                      }}
                      onBlur={(e) => {
                        validateField(
                          "otherRefundReason",
                          formValue.refundReason,
                          e.target.value
                        );
                      }}
                      onFocus={() => {
                        hideErrorField("otherRefundReason");
                      }}
                      placeholder="Enter your reason for a refund"
                      symbolsLimit={250}
                      errorMsg={formError.otherRefundReason}
                    />
                  )}
                </div>
              </div>

              <div className="form-questionnaire">
                <RefundQuestionnaire
                  title="Have you activated your Digital Key?"
                  name="activateDigitalKey"
                  handleValue={(e) => {
                    handleChange("activateDigitalKey", e.target.value);
                    validateField("activateDigitalKey", e.target.value);
                  }}
                  errorMsg={formError.activateDigitalKey}
                />
              </div>
              <div className="form-addition-notes">
                <p className="form-addition-notes__title">Additional notes</p>
                <RefundTextareaField
                  id="additionNotes"
                  value={formValue.additionNotes}
                  handleValue={(e) => {
                    handleChange("additionNotes", e.target.value);
                    validateField("additionNotes", formValue.additionNotes);
                  }}
                  onBlur={() => {
                    validateField("additionNotes", formValue.additionNotes);
                  }}
                  onFocus={() => {
                    hideErrorField("additionNotes");
                  }}
                  placeholder="Enter your notes"
                  symbolsLimit={300}
                  errorMsg={formError.additionNotes}
                />
              </div>
              <div className="form-approve-policy">
                <input
                  id="approve_policy"
                  type="checkbox"
                  checked={formValue.approvePolicy}
                  onChange={() => {
                    handleChange("approvePolicy", !formValue.approvePolicy);
                  }}
                  onBlur={() => {
                    validateField("approvePolicy", formValue.approvePolicy);
                  }}
                  onFocus={() => {
                    hideErrorField("approvePolicy");
                  }}
                />
                <label
                  className={
                    formError.approvePolicy !== "" ? "label-error" : ""
                  }
                  htmlFor="approve_policy"
                >
                  I confirm the refund request, as well as I agree with the
                  Refund Policy and that the funds will be <br /> returned to
                  the account which was used for payment.{" "}
                  <span
                    className={formError.approvePolicy !== "" ? "error" : ""}
                  >
                    *
                  </span>
                </label>
              </div>
              <button
                type="submit"
                className={`button ${
                  !checkformValid() ? "button--novalid" : ""
                }`}
                disabled={refundIsLoading}
              >
                submit
              </button>
            </form>
            <RefundErrorModal
              isOpen={showError}
              errorMsg={refundError}
              onCancel={() => setShowError(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}

RefundForm.propTypes = {
  userData: PropTypes.objectOf(PropTypes.string),
};
RefundForm.defaultProps = {
  userData: null,
};

const mapStateToProps = (state) => ({
  userData: state.userDataStore.userData,
  dataIsLoading: state.userDataStore.userDataIsLoading,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(RefundForm);
